import { useCallback } from "react"
import axios from "axios"
import { delay } from "../../utils/delay"
import { useAxiosInstances } from "../axios-instance"
import { IAcesso, IAuthentication, IExtendedUsuario } from "../../application/models"

export const useAuthApi = () => {
  // Axios
  const { axiosInstance, axiosAuthInstance } = useAxiosInstances()

  const loginApi = useCallback(async (data: {
    loginCliente: string
    loginUsuario: string
    senhaUsuario: string
    idProduto: number
  }): Promise<{
    token: string
    authentication: IAuthentication
    usuario: IExtendedUsuario
  }> => {
    try {
      const result = await axiosInstance.post('/auth/login', {
        loginCliente: data.loginCliente,
        loginUsuario: data.loginUsuario,
        senhaUsuario: data.senhaUsuario,
        idProduto: data.idProduto
      })

      return {
        authentication: result.data.authentication,
        token: result.data.accessToken,
        usuario: result.data.usuario
      }
    } catch (error) {
      throw error
    }
  }, [axiosInstance])

  const insertAcessoApi = useCallback(async (data: {
    IdProduto: number
    IdUsuario: number
  }): Promise<{
    token: string
    acesso: IAcesso
    usuario: IExtendedUsuario
  }> => {
    try {
      const result = await axiosAuthInstance.post('/auth/acesso', {
        IdProduto: data.IdProduto,
        IdUsuario: data.IdUsuario
      })

      return {
        acesso: result.data.acesso,
        token: result.data.accessToken,
        usuario: result.data.usuario
      }
    } catch (error) {
      throw error
    }
  }, [axiosAuthInstance])

  const generateLegacyRedirectApi = useCallback(async (data: {
    IdAcesso: number
    IdProduto: number
  }): Promise<{
    redirectUrl: string
    method: 'GET' | 'POST' | 'GET_NOREFERRER'
  }> => {
    try {
      const result = await axiosAuthInstance.post('/auth/legacy-redirect', {
        IdAcesso: data.IdAcesso,
        IdProduto: data.IdProduto
      })

      return {
        redirectUrl: result.data.redirectUrl,
        method: result.data.method
      }
    } catch (error) {
      throw error
    }
  }, [axiosAuthInstance])

  const requeNewPasswordApi = useCallback(async (data: {
    email: string
  }) => {
    try {
      const result = await axiosInstance.post('/usuario/request-password-recovery', {
        email: data.email
      })
      return result.data
    } catch (error: any) {
      // O backend sempre retorna sucesso por segurança, mas pode ter erro na requisição
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message)
      }
      throw error
    }
  }, [axiosInstance])


  const validateCodeApi = useCallback(async (data: {
    email: string
    code: string
  }): Promise<boolean> => {
    try {
      const result = await axiosInstance.post('/usuario/validate-recovery-code', {
        email: data.email,
        code: data.code
      })
      return result.data.valid === true
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message)
      }
      throw error
    }
  }, [axiosInstance])

  const createNewPasswordApi = useCallback(async (data: {
    code?: string // HashUrl do link do email
    email?: string
    codigo?: string // Código de 6 dígitos
    password: string
  }):Promise<void> => {
    try {
      if (data.code) {
        // Fluxo com código do link do email (HashUrl)
        const result = await axiosInstance.post('/usuario/reset-password', {
          code: data.code,
          newPassword: data.password
        })
        return result.data
      } else if (data.email && data.codigo) {
        // Fluxo com código de verificação de 6 dígitos
        const result = await axiosInstance.post('/usuario/reset-password', {
          email: data.email,
          codigo: data.codigo,
          newPassword: data.password
        })
        return result.data
      } else {
        throw new Error('É necessário fornecer código ou email+código de verificação')
      }
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message)
      }
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error)
      }
      throw error
    }
  }, [axiosInstance])

  const refreshApi = useCallback(async (token: string) => {
    try {
      const authUrl = process.env.REACT_APP_VELES_AUTH_URL
      const response = await axios.get(`${authUrl}/api/validation/refreshTokenv3`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      return response.data
    } catch (error) {
      throw error
    }
  }, [])

  const getSessionTimeoutApi = useCallback(async (): Promise<{ sessionTimeoutSeconds: number; idVelesCliente: number }> => {
    try {
      const response = await axiosAuthInstance.get('/auth/session-timeout')
      return response.data
    } catch (error) {
      throw error
    }
  }, [axiosAuthInstance])

  return {
    loginApi,
    insertAcessoApi,
    generateLegacyRedirectApi,
    requeNewPasswordApi,
    validateCodeApi,
    createNewPasswordApi,
    refreshApi,
    getSessionTimeoutApi
  }
}